import moment from 'moment';
import 'moment-timezone';

const UTC_RESULT = { offsetHours: 0, offsetMinutes: 0, ianaZone: 'UTC', isHourly: true, isDaylightSaving: false, isKnown: false };

/**
 * Resolves a Windows timezone id to the whole-hour UTC offset in effect at `now`.
 * The grid can only shift by whole hours, so zones on a fractional offset report `isHourly: false`
 * and an `offsetHours` of 0 so the caller displays UTC.
 */
export function resolveOffset(timeZoneId, mapping, now = new Date()) {
  const ianaZone = timeZoneId ? mapping[timeZoneId] : undefined;
  if (!ianaZone) {
    return { ...UTC_RESULT };
  }
  const instant = moment.tz(now, ianaZone);
  const offsetMinutes = instant.utcOffset();
  const isHourly = offsetMinutes % 60 === 0;
  return {
    offsetHours: isHourly ? offsetMinutes / 60 : 0,
    offsetMinutes,
    ianaZone,
    isHourly,
    isDaylightSaving: instant.isDST(),
    isKnown: true,
  };
}

/**
 * The timezone list names every zone by its standard offset and standard name,
 * e.g. `(UTC-05:00) Eastern Time (US & Canada)`.
 * While daylight saving time is in effect this returns the name for the current offset,
 * e.g. `(UTC-04:00) Eastern Daylight Time (US & Canada)`.
 */
export function formatZoneDisplay(display, { offsetMinutes, isDaylightSaving }) {
  if (!isDaylightSaving) {
    return display;
  }
  const sign = offsetMinutes < 0 ? '-' : '+';
  const abs = Math.abs(offsetMinutes);
  const hours = String(Math.floor(abs / 60)).padStart(2, '0');
  const minutes = String(abs % 60).padStart(2, '0');
  return display
    .replace(/^\(UTC[^)]*\)/, `(UTC${sign}${hours}:${minutes})`)
    .replace(/ Time(?= \()/, ' Daylight Time');
}

export function formatUtcOffset(offsetMinutes) {
  const sign = offsetMinutes < 0 ? '-' : '+';
  const abs = Math.abs(offsetMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  return minutes === 0
    ? `UTC${sign}${hours}`
    : `UTC${sign}${hours}:${String(minutes).padStart(2, '0')}`;
}
